import { defineComponent, h, computed, onMounted, onUnmounted } from 'vue';
import { tag } from '@/ui/components/Tag';
import { t } from '@/ui/i18n/i18n';
import { useLocalStorage } from '@/services/storage';
import differenceInDays from 'date-fns/differenceInDays';
import { EventBus } from '@/ui/EventBus';
import { Events } from '@/ui/events';

export enum FeatureNames {
  ADD_ACTION_TO_INSPECTION = 'addActionInspection',
  ADD_ACTION_TO_CHECKLIST = 'addActionChecklist',
  ENTITY_FOLDERS = 'entityFolders',
  INSPECTION_LOGS = 'inspectionLogs',
  ENABLE_ENTITY_ITEM_RESTRICTIONS = 'enableEntityItemRestrictions',
  REFERRAL_PROGRAM = 'referralProgram'
}

interface NewFeatureProps {
  featureName: FeatureNames;
  classes?: string;
}

// These are the dates of the release of the feature (can have an error range of a few days)
// After one month we won't be showing the `New` tag anymore to tell the user
// that this is a new feature.
const featureReleaseDates: {[k in FeatureNames]: Date} = {
  [FeatureNames.INSPECTION_LOGS]: new Date(2022, 0, 20),
  [FeatureNames.ADD_ACTION_TO_INSPECTION]: new Date(2022, 0, 25),
  [FeatureNames.ADD_ACTION_TO_CHECKLIST]: new Date(2022, 0, 25),
  [FeatureNames.ENTITY_FOLDERS]: new Date(2022, 1, 10),
  [FeatureNames.ENABLE_ENTITY_ITEM_RESTRICTIONS]: new Date(2022, 1, 10),
  [FeatureNames.REFERRAL_PROGRAM]: new Date(2022, 5, 28),
};

type INewFeature = { hasSeenFeature: boolean; expirationDate: Date };

const expirationDateFeature = (date: Date): Date => {
  const dateCopy = new Date(date);
  dateCopy.setMonth(dateCopy.getMonth() + 1);

  return dateCopy;
};

export const hasNewFeature = (featureName: FeatureNames) => {
  const futureExpirationDateMemoized = expirationDateFeature(featureReleaseDates[featureName]);

  const defaultValue: INewFeature = {
    hasSeenFeature: false,
    expirationDate: futureExpirationDateMemoized,
  };
  const { value: storage } = useLocalStorage(featureName, JSON.stringify(defaultValue));
  const storageParsed = JSON.parse(storage.value as string);

  if (storageParsed.hasSeenFeature) {
    return false;
  }

  // After one month if the user didn't click the button
  // to see the new feature the badge should disappear
  const days = differenceInDays(futureExpirationDateMemoized, new Date());

  return days > 0 && days < 31;
};

export const NewFeature = defineComponent<NewFeatureProps>({
  name: 'NewFeature',
  props: ['featureName', 'classes'],
  setup(props) {
    if (featureReleaseDates[props.featureName] === undefined) {
      console.error('Feature name must be present in `featureReleaseDates` object');
    }

    const futureExpirationDateMemoized = expirationDateFeature(featureReleaseDates[props.featureName]);

    const defaultValue: INewFeature = {
      hasSeenFeature: false,
      expirationDate: futureExpirationDateMemoized,
    };

    const { set } = useLocalStorage(props.featureName, JSON.stringify(defaultValue));

    const showNewFeatureBadge = computed<boolean>(() => hasNewFeature(props.featureName));

    const onViewedNewFeature = (feature: FeatureNames) => {
      if (feature !== props.featureName) {
        return;
      }

      set(JSON.stringify({ hasSeenFeature: true, expirationDate: futureExpirationDateMemoized }));
    };

    const registerEvents = () => {
      EventBus.$on(Events.viewedNewFeature, onViewedNewFeature);
    };

    const unregisterEvents = () => {
      EventBus.$off(Events.viewedNewFeature, onViewedNewFeature);
    };

    onMounted(registerEvents);
    onUnmounted(unregisterEvents);

    return function render() {
      return showNewFeatureBadge.value
        ? h('div', { class: props.classes ?? '' }, [tag('primary', t('new-features.new'))])
        : null;
    };
  },
});

export function newFeature(props: NewFeatureProps, slot?: string) {
  return h(NewFeature, { props, slot });
}
